import type { AppEnv } from '@hc-frontend/core-context';
import { JourneyAlias } from '@hc-frontend/core-types';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import { v1 } from '@hc-frontend/services-journey';
import { find, keys, map, reduce, toPairs, values, whereEq } from 'ramda';

import type { SurveyAnswers, VisitRepository } from '../../domain';
import {
  EstimatedSpending,
  HouseholdIncome,
  InsureWho,
  KindOfMedicarePlan,
  PlateName,
  SeniorCheck,
} from '../../domain';
import type { Journey, Plate, PlatePath } from '../../domain/entities/journey';
import type { JourneyRepository } from '../../domain/repositories/journey-repository';
import { journeyAliasToPath } from './journey-alias-to-path';

interface PlateNameAndPathname {
  pathname?: PlatePath;
  plateName: PlateName;
}

type MedicareAdvantagePlateNameMap = Record<
  v1.MedicareAdvantagePlate,
  PlateNameAndPathname
>;

const medicareAdvantagePlateMap: MedicareAdvantagePlateNameMap = {
  redirect_typ_medicare_advantage: {
    plateName: PlateName.Enum.FLOW_CONVERSION,
    pathname: 'quotes',
  },
  'medicare_coverage_urgency.advantage': {
    plateName: PlateName.Enum.COVERAGE_URGENCY,
    pathname: 'survey/coverage-urgency',
  },
  'additional_coverage.advantage': {
    plateName: PlateName.Enum.ENROLLED_PART_A_AND_PART_B,
    pathname: 'survey/enrolled-part-a-part-b',
  },
  'fname-lname-email-location-phone.advantage': {
    plateName: PlateName.Enum.PERSONAL_INFORMATION,
    pathname: 'survey/personal-information',
  },
  'gender-dob-dob_category.advantage': {
    plateName: PlateName.Enum.GENDER_AND_DATE_OF_BIRTH,
    pathname: 'survey/gender-dob',
  },
  'zip_code.advantage': {
    plateName: PlateName.Enum.ZIP_CODE,
    pathname: 'survey/zip-code',
  },
};

type MedicareSupplementPlateNameMap = Record<
  v1.MedicareSupplementPlate,
  PlateNameAndPathname
>;

const medicareSupplementPlateMap: MedicareSupplementPlateNameMap = {
  redirect_typ_medicare: {
    plateName: PlateName.Enum.FLOW_CONVERSION,
    pathname: 'quotes',
  },
  visit_purpose: {
    plateName: PlateName.Enum.VISIT_PURPOSE,
    pathname: 'survey/visit-purpose',
  },
  medicare_coverage_urgency: {
    plateName: PlateName.Enum.COVERAGE_URGENCY,
    pathname: 'survey/coverage-urgency',
  },
  additional_coverage: {
    plateName: PlateName.Enum.ENROLLED_PART_A_AND_PART_B,
    pathname: 'survey/enrolled-part-a-part-b',
  },
  'fname-lname-email-location-phone': {
    plateName: PlateName.Enum.PERSONAL_INFORMATION,
    pathname: 'survey/personal-information',
  },
  'gender-dob-dob_category': {
    plateName: PlateName.Enum.GENDER_AND_DATE_OF_BIRTH,
    pathname: 'survey/gender-dob',
  },
  'gender.supplement': {
    plateName: PlateName.Enum.GENDER,
    pathname: 'survey/gender',
  },
  'dob-dob_category.supplement': {
    plateName: PlateName.Enum.DATE_OF_BIRTH,
    pathname: 'survey/dob',
  },
  zip_code: {
    plateName: PlateName.Enum.ZIP_CODE,
    pathname: 'survey/zip-code',
  },
  'first_time_enrollment.medicare': {
    plateName: PlateName.Enum.FIRST_TIME_ENROLLMENT_MEDICARE,
    pathname: 'survey/first-time-enrollment',
  },
};

type MedicareGenericPlateNameMap = Record<
  v1.MedicareGenericPlate,
  PlateNameAndPathname
>;

const medicareGenericPlateMap: MedicareGenericPlateNameMap = {
  kind_of_medicare_plan: {
    plateName: PlateName.Enum.KIND_OF_MEDICARE_PLAN,
    pathname: 'survey/kind-of-plan',
  },
  'medicare_coverage_urgency.advantage': {
    plateName: PlateName.Enum.COVERAGE_URGENCY,
    pathname: 'survey/coverage-urgency',
  },
  zip_code: {
    plateName: PlateName.Enum.ZIP_CODE,
    pathname: 'survey/zip-code',
  },
  'gender-dob-dob_category.skip.with_tooltip': {
    plateName: PlateName.Enum.GENDER_AND_DATE_OF_BIRTH,
    pathname: 'survey/gender-dob',
  },
  'fname-lname-email-location-phone.advantage': {
    plateName: PlateName.Enum.PERSONAL_INFORMATION,
    pathname: 'survey/personal-information',
  },
  redirect_typ_medicare: {
    plateName: PlateName.Enum.FLOW_CONVERSION,
    pathname: 'quotes',
  },
  redirect_med_advantage: {
    plateName: PlateName.Enum.REDIRECT_MEDICARE_ADVANTAGE,
    pathname: 'survey',
  },
  redirect_medicare: {
    plateName: PlateName.Enum.REDIRECT_MEDICARE_SUPPLEMENT,
    pathname: 'survey',
  },
  redirect_quotes_healthcare_com: {
    plateName: PlateName.Enum.REDIRECT_UNDER_65,
    pathname: 'survey',
  },
};

type SharedPlateMap = Record<v1.SharedPlate, PlateNameAndPathname>;

const sharedPlateMap: SharedPlateMap = {
  redirect_quotes: {
    plateName: PlateName.Enum.REDIRECT_UNDER_65,
    pathname: 'survey',
  },
};

type HealthPlateNameMap = Record<v1.HealthPlate, PlateNameAndPathname>;
const healthPlateMap: HealthPlateNameMap = {
  redirect_aca_healthcare_com: {
    plateName: PlateName.Enum.REDIRECT_ACA_HEALTHCARE_COM,
    pathname: 'survey/redirect-aca-healthcare-com',
  },
  'gender-dob-dob_category.health': {
    plateName: PlateName.Enum.GENDER_DOB_DOB_CATEGORY_HEALTH,
    pathname: 'survey/gender-dob',
  },
  coverage_duration: {
    plateName: PlateName.Enum.COVERAGE_DURATION,
    pathname: 'survey/coverage-duration',
  },
  'change_pharmacy.health': {
    plateName: PlateName.Enum.CHANGE_PHARMACY_HEALTH,
    pathname: 'survey/change-pharmacy',
  },
  estimated_spending: {
    plateName: PlateName.Enum.ESTIMATED_SPENDING,
    pathname: 'survey/estimated-spending',
  },
  'zip_code.health': {
    plateName: PlateName.Enum.ZIP_CODE_HEALTH,
    pathname: 'survey/zip-code',
  },
  household_income: {
    plateName: PlateName.Enum.HOUSEHOLD_INCOME,
    pathname: 'survey/household-income',
  },
  household_size: {
    plateName: PlateName.Enum.HOUSEHOLD_SIZE,
    pathname: 'survey/household-size',
  },
  'household_size-household_income': {
    plateName: PlateName.Enum.HOUSEHOLD_SIZE_INCOME,
    pathname: 'survey/household-size-income',
  },
  redirect_typ_health: {
    plateName: PlateName.Enum.REDIRECT_TYP_HEALTH,
    pathname: 'survey/redirect-typ-health',
  },
  income_subsidy: {
    plateName: PlateName.Enum.INCOME_SUBSIDY,
    pathname: 'survey/income-subsidy',
  },
  quality_life_event: {
    plateName: PlateName.Enum.QUALIFYING_LIFE_EVENT,
    pathname: 'survey/qle',
  },
  redirect_stm_healthcare_com: {
    plateName: PlateName.Enum.REDIRECT_STM_HEALTHCARE_COM,
    pathname: 'survey/redirect-stm-healthcare-com',
  },
  insure_who: {
    plateName: PlateName.Enum.INSURE_WHO,
    pathname: 'survey/insure-who',
  },
  redirect_medicare_healthcare_com: {
    plateName: PlateName.Enum.REDIRECT_MEDICARE_HEALTHCARE_COM,
    pathname: 'survey/redirect-medicare-healthcare-com',
  },
  spouse_children: {
    plateName: PlateName.Enum.SPOUSE_CHILDREN,
    pathname: 'survey/spouse-children',
  },
  immediate_coverage: {
    plateName: PlateName.Enum.IMMEDIATE_COVERAGE,
    pathname: 'survey/immediate-coverage',
  },
  'fname-lname-email-location-phone.health': {
    plateName: PlateName.Enum.FNAME_LNAME_EMAIL_LOCATION_PHONE_HEALTH,
    pathname: 'survey/personal-information',
  },
  tobacco: {
    plateName: PlateName.Enum.TOBACCO,
    pathname: 'survey/tobacco',
  },
  'senior_check.health': {
    plateName: PlateName.Enum.SENIOR_CHECK_HEALTH,
    pathname: 'survey/senior-check',
  },
  'first_time_enrollment.health': {
    plateName: PlateName.Enum.FIRST_TIME_ENROLLMENT_HEALTH,
    pathname: 'survey/first-time-enrollment',
  },
};

type DtoPlateNameToEntityPlateNameMap = Record<
  v1.PlateName,
  PlateNameAndPathname
>;

const plateConfiguration: DtoPlateNameToEntityPlateNameMap = {
  ...medicareAdvantagePlateMap,
  ...medicareGenericPlateMap,
  ...medicareSupplementPlateMap,
  ...healthPlateMap,
  ...sharedPlateMap,
};

function getFullRoutesForPlates<T extends Record<string, PlateNameAndPathname>>(
  journeyAlias: JourneyAlias,
  plates: T,
): Record<PlateName, string> {
  return Object.fromEntries(
    map(
      (v) => [
        v.plateName,
        `/${journeyAliasToPath[journeyAlias]}/${v.pathname}`,
      ],
      values(plates),
    ),
  ) as Record<PlateName, string>;
}

function getPlateIds<T extends Record<string, PlateNameAndPathname>>(
  plates: T,
): Record<PlateName, string> {
  return Object.fromEntries(
    Object.entries(plates).map(([key, value]) => [value.plateName, key]),
  ) as Record<PlateName, string>;
}

const routeConfiguration = {
  MEDICARE: getFullRoutesForPlates(
    JourneyAlias.Enum.MEDICARE_GENERIC,
    medicareGenericPlateMap,
  ),
  MEDICARE_ADVANTAGE: getFullRoutesForPlates(
    JourneyAlias.Enum.MEDICARE_ADVANTAGE,
    medicareAdvantagePlateMap,
  ),
  MEDICARE_SUPPLEMENT: getFullRoutesForPlates(
    JourneyAlias.Enum.MEDICARE_SUPPLEMENT,
    medicareSupplementPlateMap,
  ),
  HEALTH: getFullRoutesForPlates(JourneyAlias.Enum.HEALTH_DEFAULT, {
    ...sharedPlateMap,
    ...healthPlateMap,
  }),
} as const;

const QuestionConfiguration: Partial<Record<v1.Question, keyof SurveyAnswers>> =
  {
    ask_for_zip_code: 'ASK_FOR_ZIP_CODE',
    dob_category: 'DATE_OF_BIRTH_CATEGORY',
    insure_who: 'INSURE_WHO',
    estimated_spending: 'ESTIMATED_SPENDING',
    kind_of_medicare_plan: 'KIND_OF_MEDICARE_PLAN',
    senior_check: 'SENIOR_CHECK',
    is_oep: 'IS_OEP',
    household_income: 'HOUSEHOLD_INCOME',
    is_phoenix: 'IS_PHOENIX',
  };

function findOrder(
  plateName: v1.PlateName,
  searchPlate: v1.PlateName,
  response: v1.GetJourneyResponseDto,
): number {
  if (plateName === searchPlate) {
    return 1;
  }

  const transitions = response.plates[plateName]?.transitions;

  let currentOrder = 0;
  transitions?.forEach((transition) => {
    const order = findOrder(transition.plate, searchPlate, response) + 1;
    if (order > currentOrder) {
      currentOrder = order;
    }
  });
  return currentOrder;
}

const journeyAliasToIdMap: Record<JourneyAlias, v1.JourneyId> = {
  HEALTH_DEFAULT: v1.JourneyId.Enum['health-default'],
  MEDICARE_ADVANTAGE: v1.JourneyId.Enum['medicare-advantage'],
  MEDICARE_GENERIC: v1.JourneyId.Enum['medicare-generic'],
  MEDICARE_SUPPLEMENT: v1.JourneyId.Enum['medicare-supplement'],
  '106': v1.JourneyId.Enum['106'],
  '107': v1.JourneyId.Enum['107'],
  '108': v1.JourneyId.Enum['108'],
};

function mapJourneyId(journeyAlias: JourneyAlias) {
  return journeyAliasToIdMap[journeyAlias];
}

export function mapQuestionsToEntity(
  questions: v1.TransitionDto['questions'],
): Partial<SurveyAnswers> {
  return reduce(
    (accum, current) => {
      let value;
      if (current === v1.Question.Enum.ask_for_zip_code) {
        value = Boolean(questions[current]);
      }
      if (current === v1.Question.Enum.dob_category) {
        value = questions[current] === 'u65' ? 'UNDER_65' : 'OVER_65';
      }
      if (current === v1.Question.Enum.is_phoenix) {
        value = questions[current] === 'true';
      }
      if (current === v1.Question.Enum.is_oep) {
        value = questions[current] === 'is-oep-yes';
      }
      if (current === v1.Question.Enum.kind_of_medicare_plan) {
        const key = questions[current];
        const answersMap: Record<v1.KindOfMedicarePlan, KindOfMedicarePlan> = {
          [v1.KindOfMedicarePlan.Enum.medicare_advantage]:
            KindOfMedicarePlan.Enum.MEDICARE_ADVANTAGE,
          [v1.KindOfMedicarePlan.Enum.medicare_supplement_medigap]:
            KindOfMedicarePlan.Enum.MEDICARE_SUPPLEMENT_MEDIGAP,
          [v1.KindOfMedicarePlan.Enum.not_sure]:
            KindOfMedicarePlan.Enum.NOT_SURE,
        };
        if (key) {
          value = answersMap[key];
        }
      }
      if (current === v1.Question.Enum.estimated_spending) {
        const key = questions[current];
        const answersMap: Record<v1.EstimatedSpending, EstimatedSpending> = {
          [v1.EstimatedSpending.Enum.low]: EstimatedSpending.Enum.LOW,
          [v1.EstimatedSpending.Enum.medium]: EstimatedSpending.Enum.MEDIUM,
          [v1.EstimatedSpending.Enum.high]: EstimatedSpending.Enum.HIGH,
        };
        if (key) {
          value = answersMap[key];
        }
      }
      if (current === v1.Question.Enum.insure_who) {
        const key = questions[current];
        const answersMap: Record<v1.InsureWho, InsureWho> = {
          [v1.InsureWho.Enum.just_me]: InsureWho.Enum.JUST_ME,
          [v1.InsureWho.Enum.myself_and_others]:
            InsureWho.Enum.MYSELF_AND_OTHERS,
        };
        if (key) {
          value = answersMap[key];
        }
      }
      if (current === v1.Question.Enum.senior_check) {
        const key = questions[current];
        const answersMap: Record<v1.SeniorCheck, SeniorCheck> = {
          [v1.SeniorCheck.Enum.o65]: SeniorCheck.Enum.O65,
          [v1.SeniorCheck.Enum.u65]: SeniorCheck.Enum.U65,
        };
        if (key) {
          value = answersMap[key];
        }
      }
      if (current === v1.Question.Enum.household_income) {
        const key = questions[current];
        const answersMap: Record<v1.HouseholdIncome, HouseholdIncome> = {
          [v1.HouseholdIncome.Enum.below]: HouseholdIncome.Enum.BELOW,
          [v1.HouseholdIncome.Enum.between]: HouseholdIncome.Enum.BETWEEN,
          [v1.HouseholdIncome.Enum.over]: HouseholdIncome.Enum.OVER,
        };
        if (key) {
          value = answersMap[key];
        }
      }

      return { ...accum, [QuestionConfiguration[current] as string]: value };
    },
    {},
    keys(questions),
  );
}

function mapDtoToEntity(journey: v1.GetJourneyResponseDto): Journey {
  const parsedPlates: Journey['plates'] = reduce(
    (acc, current) => {
      const transitions = journey.plates[current]?.transitions ?? [];

      const parsedTransitions: Plate['transitions'] = map((transition) => {
        const { plateName } = plateConfiguration[transition.plate];
        return {
          plate: plateName,
          validate: transition.validate,
          questions: mapQuestionsToEntity(transition.questions),
        };
      }, transitions);

      const { plateName, pathname } = plateConfiguration[current];
      const plate: Plate = {
        name: plateName,
        order: findOrder(journey.start, current, journey),
        transitions: parsedTransitions,
        pathname,
      };

      const parsedPlateName = plateName;

      return { ...acc, [parsedPlateName]: plate };
    },
    {},
    keys(journey.plates),
  );

  const parsedJourney: Journey = {
    id: Number(journey.id),
    start: plateConfiguration[journey.start].plateName,
    plates: parsedPlates,
  };

  return parsedJourney;
}

export type GetJourneyAdapterOptions = {
  journeyServiceBaseUrl: string;
  healthcareSiteBaseUrl: string;
  reflexiveJourneyBaseUrl: string;
  appEnv: AppEnv;
};
export function moveToPlateAdapterFactory({
  journeyServiceBaseUrl,
  healthcareSiteBaseUrl,
  reflexiveJourneyBaseUrl,
  appEnv,
}: GetJourneyAdapterOptions): Pick<
  JourneyRepository,
  'getJourney' | 'moveToPlate' | 'getNextPlate' | 'getCurrentPlate'
> &
  VisitRepository {
  return {
    getJourney: async (journeyAlias) => {
      try {
        const options: v1.GetJourneyRequestDto = {
          journeyId: mapJourneyId(journeyAlias),
          env: appEnv === 'prd' ? 'production' : 'development',
        };

        const result = await v1.getJourney(journeyServiceBaseUrl, options);

        if (result.status !== 200 || result.body === undefined) {
          throw new Error(`Failed to fetch journey with code ${result.status}`);
        }

        return createSuccessOperationResult(mapDtoToEntity(result.body));
      } catch (error) {
        return createErrorOperationResult({
          errors: [{ code: 'failedToGetJourney', rootCause: error as Error }],
        });
      }
    },

    getNextPlate: async ({ currentPlate, journey, answers }) => {
      try {
        const { plates, start } = journey;

        let plateToMove = plates[start];

        if (currentPlate) {
          const transitions = plates[currentPlate]?.transitions ?? [];

          const plateTransition = find(
            (transition) => whereEq(transition.questions, answers),
            transitions,
          );
          plateToMove = plateTransition
            ? plates[plateTransition.plate]
            : undefined;
        }
        if (!plateToMove) {
          throw new Error(
            `Could not found a valid transition for plate ${currentPlate} on the Journey configuration`,
          );
        }

        return createSuccessOperationResult(plateToMove);
      } catch (error) {
        return createErrorOperationResult({
          errors: [{ code: 'failedToGetNextPlate', rootCause: error as Error }],
        });
      }
    },

    moveToPlate: async ({ journeyAlias, plate, targetsExternalApp }) => {
      try {
        const baseAppUrl = targetsExternalApp ? healthcareSiteBaseUrl : '';
        const journeyPath = journeyAliasToPath[journeyAlias];

        const url = `${baseAppUrl}/${journeyPath}/${plate?.pathname ?? ''}`;

        if (!targetsExternalApp) {
          const { redirect } = await import('next/navigation');
          redirect(url);
        }
        return createSuccessOperationResult(url);
      } catch (error) {
        const rootCauseError = error as Error;
        if (rootCauseError.message.includes('NEXT_REDIRECT')) {
          throw rootCauseError;
        }
        return createErrorOperationResult({
          errors: [{ code: 'failedToMoveToPlate', rootCause: rootCauseError }],
        });
      }
    },

    getCurrentPlate: async () => {
      try {
        const { headers } = await import('next/headers');

        const pathname = headers()
          .get('x-pathname')
          ?.replace(/\/$/, '') as string;

        const routes = toPairs(routeConfiguration.MEDICARE)
          .concat(toPairs(routeConfiguration.MEDICARE_ADVANTAGE))
          .concat(toPairs(routeConfiguration.MEDICARE_SUPPLEMENT))
          .concat(toPairs(routeConfiguration.HEALTH));

        const currentPlate = find(
          ([, route]) => pathname.endsWith(route),
          routes,
        );

        if (!currentPlate) {
          throw new Error('Could not find the current plate');
        }

        return createSuccessOperationResult(currentPlate[0]);
      } catch (error) {
        return createErrorOperationResult({
          errors: [
            { code: 'failedToGetCurrentPlate', rootCause: error as Error },
          ],
        });
      }
    },
    async saveVisit({
      domainSessionId,
      plateId,
      product,
      journeyId,
      journeyAlias,
      sessionId,
      userId,
    }) {
      try {
        const plateLookup: Record<
          JourneyAlias,
          Partial<Record<PlateName, string>>
        > = {
          [JourneyAlias.Enum.HEALTH_DEFAULT]: getPlateIds(healthPlateMap),
          [JourneyAlias.Enum.MEDICARE_GENERIC]: getPlateIds(
            medicareGenericPlateMap,
          ),
          [JourneyAlias.Enum.MEDICARE_ADVANTAGE]: getPlateIds(
            medicareAdvantagePlateMap,
          ),
          [JourneyAlias.Enum.MEDICARE_SUPPLEMENT]: getPlateIds(
            medicareSupplementPlateMap,
          ),
          [JourneyAlias.Enum['106']]: getPlateIds(medicareSupplementPlateMap),
          [JourneyAlias.Enum['107']]: getPlateIds(healthPlateMap),
          [JourneyAlias.Enum['108']]: getPlateIds(medicareSupplementPlateMap),
        };

        const result = await v1.saveVisit(reflexiveJourneyBaseUrl, {
          domain_session_id: domainSessionId,
          journey_id: journeyId.toString(),
          plate_id: plateLookup[journeyAlias][plateId] as string, // Let the service handle the validation
          product,
          session_id: sessionId,
          user_id: userId,
        });

        if (result.result !== 'success') {
          throw new Error(
            `Error saving user's visit with code ${result.status}`,
          );
        }

        return createSuccessOperationResult(undefined);
      } catch (error) {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToSaveVisit',
              rootCause: error as Error,
            },
          ],
        });
      }
    },
  };
}
